import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import type { cardApiSpec } from "@wingspanhq/wingspan-api-types";
import { ActiveCard } from "../../../Wallet/components/BankCard/ActiveCard";
import { EmptyCardPlaceholder } from "../../../Wallet/components/BankCard/EmptyCardPlaceholder";
import { ProcessCardPlaceholder } from "../../../Wallet/components/BankCard/ProcessCardPlaceholder";
import { useActivateCardModal } from "../../../Wallet/modals/ActivateCard/useActivateCardModal";
import { useCardActivatedModal } from "../../../Wallet/modals/ActivateCard/useCardActivatedModal";
import { useConfirmReceivingCardModal } from "../../../Wallet/modals/ActivateCard/useConfirmRecivingCardModal";
import { useReactivateCardModal } from "../../../Wallet/modals/ReactivateCard/useReactivateCardModal";
import { selectorCardName } from "../../../Wallet/selectors/selectorCardName";
import { WalletCardType } from "../../../Wallet/types";
import { sendCardUpdatedToParent } from "./walletCardEvents";

type Props = {
  cardType: WalletCardType;
  card?: cardApiSpec.Card;
  onOrder: () => void;
};

export const EmbeddedWalletCardSlot: React.FC<Props> = ({
  cardType,
  card,
  onOrder
}) => {
  const { openSnackbar } = useWSSnackbar();
  const confirmReceivingCardModal = useConfirmReceivingCardModal();
  const activateCardModal = useActivateCardModal();
  const cardActivatedModal = useCardActivatedModal();
  const reactivateCardModal = useReactivateCardModal();

  if (!card || card.status === "Closed") {
    return (
      <EmptyCardPlaceholder
        cardType={cardType}
        actionIcon="card"
        actionLabel="Order free card"
        onAction={onOrder}
      />
    );
  }

  const cardId = card.cardId;
  const cardName = selectorCardName(card);

  const activatePhysicalCard = () => {
    confirmReceivingCardModal.open({
      onSubmit() {
        activateCardModal.open({
          cardId,
          onSuccess() {
            openSnackbar({ message: "Card activated", type: "success" });
            sendCardUpdatedToParent(cardType, "activated");
            cardActivatedModal.open({ onContinue: () => {} });
          }
        });
      }
    });
  };

  const reactivateCard = () => {
    reactivateCardModal.open({
      cardId,
      cardName,
      onSuccess() {
        openSnackbar({ message: "Card reactivated", type: "success" });
        sendCardUpdatedToParent(cardType, "reactivated");
      }
    });
  };

  switch (card.status) {
    case "Active":
      return <ActiveCard card={card} />;
    case "Inactive":
      return (
        <ProcessCardPlaceholder
          cardType={cardType}
          title="Card suspended"
          label="Reactivate card"
          onAction={reactivateCard}
        />
      );
    case "PendingActivation":
    case "PendingFulfillment":
      return (
        <ProcessCardPlaceholder
          cardType={cardType}
          title={
            cardType === WalletCardType.Physical
              ? "On its way"
              : "Pending activation"
          }
          label="Activate card"
          onAction={
            cardType === WalletCardType.Physical
              ? activatePhysicalCard
              : onOrder
          }
        />
      );
    default:
      return (
        <ProcessCardPlaceholder cardType={cardType} title="One time card" />
      );
  }
};
